// English

export default {
  app: {
    name: 'Replay Shield',
    shortSlogan: 'Watch replays in peace.'
  },

  // Product copy — single source of truth. Every page/SEO/JSON-LD product intro
  // must reference this block via `@:product.shortIntro` / `@:product.longIntro` /
  // `@:product.aboutIntro` / `@:product.capabilities`. Do not re-author the same
  // copy elsewhere. See docs/mainadr/0008-shared-product-copy.md.
  product: {
    shortIntro: 'Replay Shield is a browser extension (add-on) that automatically hides spoilers (scores, thumbnails, titles, comments) on sports replay pages. Available for Chrome, Edge, and Firefox.',
    longIntro: 'Replay Shield is a browser extension (add-on) that automatically hides spoilers (scores, thumbnails, titles, comments, etc.) on sports replay pages, so you can enjoy the replay without any spoilers getting in the way!',
    aboutIntro: 'Replay Shield is a browser extension that automatically hides sports scores and spoilers on major sports video sites, so you can keep the live-broadcast suspense when watching replays or highlights. All detection and hiding logic runs locally in your browser — data never leaves your device.',
    capabilities: [
      'One-click toggle: master switch flips all supported sites at once',
      'Info hiding: automatically hides titles, thumbnails, scores, and comments',
      'Loading mask: progress overlay lifts only after the page finishes loading',
      'Intro cover: a panel covers the player before playback to block opening spoilers',
      'Local-first: all logic runs inside your browser; preferences never leave your device'
    ]
  },

  slogan: 'Replay Shield for Sports Videos',

  nav: {
    home: 'Home',
    features: 'Features',
    install: 'Install',
    guide: 'How to use',
    matrix: 'Terminal Support',
    faq: 'FAQ',
    about: 'About',
    privacy: 'Privacy',
    terms: 'Terms',
    download: 'Download',
    login: 'Sign in',
    account: 'Account',
    accountCenter: 'My Account',
    subscribe: 'Pro membership',
    upgradeCta: 'Get Pro membership'
  },

  seo: {
    siteName: 'Replay Shield',
    shortName: 'Replay Shield',
    description: '@:product.shortIntro',
    keywords: 'Replay Shield,spoiler blocker,sports replay,score hider,browser extension,Chrome extension,Edge extension,Firefox extension',
    ogImage: '/og/default.png',

    home: {
      title: 'Replay Shield — Hide sports replay spoilers',
      description: '@:product.longIntro'
    },
    install: {
      title: 'Install Guide — Replay Shield',
      description: 'One-click install from the Chrome, Edge, and Firefox stores, plus manual .crx install for 360, QQ, Liebao, Opera, and other Chromium-based browsers.'
    },
    guide: {
      title: 'How to Use — Replay Shield',
      description: 'Installed Replay Shield — now what? Three steps into the management console, then a deep dive into Match Navigation and every viewing setting (overlay, intro cover, progress bar protection and more). Works the same for football and basketball.'
    },
    about: {
      title: 'About Replay Shield',
      description: '@:product.aboutIntro'
    },
    subscribe: {
      title: 'Pricing — Replay Shield',
      description: 'Replay Shield Pro: the free tier covers third-party match navigation; Pro membership unlocks domestic & international match navigation and full spoiler protection. New users get a {days}-day Pro trial on sign-up; monthly / quarterly / yearly plans, invite rewards for both sides, and redeem-code support.',
      descriptionMeta: 'Replay Shield Pro: the free tier covers third-party match navigation; Pro membership unlocks domestic & international match navigation and full spoiler protection. New users get a Pro trial on sign-up; monthly / quarterly / yearly plans, invite rewards for both sides, and redeem-code support.'
    },
    login: {
      title: 'Sign in — Replay Shield',
      description: 'Sign in to Replay Shield to sync your viewing protection settings and Pro membership; supports email & password sign-in plus QQ, Douyin and Google.'
    },
    account: {
      title: 'Account — Replay Shield',
      description: 'Manage your Replay Shield account: membership, orders, invite rewards and account security.'
    },
    privacy: {
      title: 'Privacy Policy — Replay Shield',
      description: 'Replay Shield respects and protects the privacy of all users. This policy explains how we collect, use, and safeguard your information.'
    },
    terms: {
      title: 'Terms of Service — Replay Shield',
      description: 'Replay Shield Terms of Service. Please read carefully — using the product means you agree to all terms.'
    },
    refund: {
      title: 'Refund Policy — Replay Shield',
      description: 'Replay Shield Pro refund policy: virtual digital goods are not eligible for no-reason full refunds; after verification, special cases may refund pro-rata by used days.'
    },
    inviteRules: {
      title: 'Invite Rules — Replay Shield',
      description: 'Replay Shield invite rules: refer a friend and both of you earn rewards — +5 days on registration and half the plan length when they subscribe for the first time.'
    },

  notFound: {
      title: 'Page Not Found — Replay Shield',
      description: 'The page you visited does not exist or has been removed.'
    }
  },

  home: {
    hero: {
      title: 'Hidden Scores. Live Thrills.',
      subtitle: 'Watch replays in peace.',
      painLine: "Can't stay up late? Spoiled by replays?",
      intro: '@:product.longIntro',
      sellPoints: [
        'Spoilers hidden automatically',
        'Football & basketball',
        'Free sports navigation companion'
      ],
      cta: {
        recommendedFor: 'Recommended for your browser',
        detectedCta: 'Open {browser} store',
        storeGeneric: 'Get it from the store',
        installGuide: 'View install guide',
        fallbackHint: 'Not {browser}? Pick another browser',
        fallbackHintGeneric: 'Explore other install options',
        fallbackLabel: 'Choose browser',
        manualCta: 'Other browsers — manual install',
        manualHint: 'For 360 Speed, QQ, Sogou, Liebao, Opera, UC, etc.',
        availableOn: 'Available on',
        seeAll: 'See all download options',
        crx: 'View manual guide',
        stores: {
          chrome: 'Chrome Store',
          edge: 'Edge Store',
          firefox: 'Firefox Store'
        }
      }
    },

    sections: {
      features: 'Features',
      matrix: 'Terminal Support',
      preview: 'Before / After',
      quickStart: 'Quick Start',
      faq: 'FAQ',
      sites: 'Supported Sites',
      sitesSubtitle: 'Football and basketball replays on all of these major platforms get spoiler hiding',
      useCases: 'Who needs Replay Shield'
    },

    features: {
      title: 'Features',
      subtitle: 'Everything you need to watch sports replays — football and basketball alike',
      cards: [
        {
          id: 'match-list',
          title: 'Match navigation',
          subtitle: 'One-click viewing',
          desc: 'Browse top football and basketball competitions (World Cup / UCL / Premier League / La Liga / NBA / CBA) in one place. Each match lists full replays, per-half or per-quarter videos, and highlights with source platform and commentator.',
          icon: 'ph:list-bold'
        },
        {
          id: 'info-hide',
          title: 'Info hiding',
          subtitle: 'Spoiler-free browsing',
          desc: 'Open a target page and titles, thumbnails, descriptions, comments, and even the browser tab TITLE are replaced. The page feels like a clean replay.',
          icon: 'ph:eye-slash-bold',
          highlight: true
        },
        {
          id: 'progressive',
          title: 'Progressive display',
          subtitle: 'You set the pace',
          desc: 'Replays and highlights show by default; extra videos stay collapsed. Watch the regular match first, then choose to expand. No spoilers until you decide.',
          icon: 'ph:caret-down-bold'
        },
        {
          id: 'mask',
          title: 'Loading overlay',
          subtitle: 'Instant spoiler shield',
          desc: 'When a page opens, a progress bar overlay covers the content to block scores and spoilers from flashing before processing finishes.',
          icon: 'ph:shield-bold'
        },
        {
          id: 'cover-intro',
          title: 'Intro cover panel',
          subtitle: 'Spoiler-free start',
          desc: 'Show a cover panel over the player for the first few seconds of a video, blocking score flashes at the start of highlights. Set the seconds in settings.',
          icon: 'lucide:monitor-play'
        },
        {
          id: 'skip-buttons',
          title: 'Seek floating buttons',
          subtitle: 'Your pace',
          desc: 'Enable floating fast-forward / rewind buttons with a custom step, so you control exactly how much to jump.',
          icon: 'ph:fast-forward-bold'
        },
        {
          id: 'progress-protection',
          title: 'Progress bar protection',
          subtitle: 'No time-based spoilers',
          desc: 'Hide the progress bar, total duration and end time, so you can not infer the match stage or result from the timeline.',
          icon: 'lucide:timer'
        }
      ],
      ctaSubscribe: {
        title: 'Some Pro membership features need a subscription',
        desc: 'Everyone can watch the latest hot football and basketball replays for free — core features included. A Pro membership unlocks spoiler hiding, intro cover, progress bar protection and more; new users get a Pro trial on sign-up.',
        cta: 'View Pro membership plans'
      }
    },

    quickStart: {
      subtitle: 'Three steps to start watching; the full per-device walkthrough lives in the install guide.',
      deviceTabs: { desktop: 'Desktop', mobile: 'Mobile' },
      notice: 'Friendly reminder: Replay Shield only hides spoilers on pages opened from the console\'s Match Navigation — pages opened any other way are not protected.',
      ctaInstall: 'Open the full install guide',
      ctaGuide: 'Features & settings explained',
      steps: {
        desktop: [
          {
            title: 'Install the extension',
            desc: 'One-click install from the Chrome / Edge / Firefox store, or a manual .crx install.'
          },
          {
            title: 'Enter the management console',
            desc: 'Click the Replay Shield icon in your toolbar, then click "Open management console" in the popup.'
          },
          {
            title: 'Watch via Match Nav',
            desc: 'Pick a football or basketball match and click through — scores and titles are hidden automatically.'
          }
        ],
        mobile: [
          {
            title: 'Install a browser & the extension',
            desc: 'On phones use Edge or Firefox: search for Replay Shield in the extensions menu and install.'
          },
          {
            title: 'Enter the management console',
            desc: 'Tap Replay Shield in the extensions menu, then tap "Open management console" in the popup.'
          },
          {
            title: 'Watch via Match Nav',
            desc: 'Pick a match and click through — spoilers are hidden automatically.'
          }
        ]
      }
    },

    useCases: {
      title: 'Who needs Replay Shield',
      subtitle: 'Real people, real situations',
      cases: [
        {
          icon: 'lucide:briefcase',
          title: 'Students and 9-to-5 workers',
          desc: 'Daytime schedules mean no late-night live matches. Next-day replays get spoiled by titles, thumbnails, and comments before you can hit play.'
        },
        {
          icon: 'ph:trophy-bold',
          title: 'Die-hard sports fans',
          desc: 'Premier League, UCL, World Cup, NBA — wherever you follow, you want the live-broadcast suspense. Replay Shield keeps replays feeling like the first watch.'
        },
        {
          icon: 'lucide:monitor-play',
          title: 'Mobile replay viewers',
          desc: 'Commute, travel, bedtime — anyone watching long replays and highlights on a phone. Full spoiler hiding works on mobile too.'
        }
      ]
    },

    whatIsYfb: {
      eyebrow: 'WHAT IS REPLAY SHIELD',
      title: 'What is Replay Shield',
      lead: 'When you miss a live sports broadcast and try to rewatch a replay, scores, titles, thumbnails, and comments are already flooded with spoilers. This always ruins the fun for sports fans!',
      pains: [
        {
          icon: 'lucide:moon',
          title: 'No late nights',
          desc: 'Weekday schedules kill the live broadcast. By the time you watch the replay, scores already flashed in titles, thumbnails, and push notifications.'
        },
        {
          icon: 'lucide:tv',
          title: 'Missed live',
          desc: 'Catching up on a World Cup final, a UCL semi, or an NBA Game 7 days later? Social feeds and news headlines have already plastered the result everywhere.'
        },
        {
          icon: 'lucide:message-square-warning',
          title: 'Surrounded by spoilers',
          desc: 'Thumbnails showing "final 3-1", titles shouting "champion crowned", comment sections cheering "Haaland is on fire" and "what a buzzer-beater" — every element is a spoiler.'
        }
      ],
      introTag: 'INTRO',
      intro: 'Replay Shield is a browser extension (add-on) that automatically hides spoilers (scores, thumbnails, titles, comments, etc.) on sports replay pages, so you can enjoy the replay without any spoilers getting in the way!'
    },

    preview: {
      title: 'Preview',
      subtitle: 'See how Replay Shield protects your viewing experience',
      beforeLabel: 'Before',
      afterLabel: 'After',
      beforeHint: 'Score is visible',
      afterHint: 'Score is hidden',
      switchHint: 'Click to switch view',
      mockPage: {
        pageTitle: 'Trending Replays',
        siteLabel: 'Demo Site',
        badgeSpoiler: 'Spoiler',
        badgeProtected: 'Protected',
        statusToggle: 'Extension status',
        statusOn: 'Enabled',
        statusOff: 'Disabled',
        placeholder: {
          title: '[Replay Shield: Title]',
          score: '[Replay Shield: Score]',
          thumbnail: '[Replay Shield: Thumbnail]',
          comment: '[Replay Shield: Comment]',
          league: '[Replay Shield: League]'
        },
        matches: [
          {
            league: 'Premier League R28',
            homeTeam: 'Man City',
            awayTeam: 'Liverpool',
            homeShort: 'MNC',
            awayShort: 'LIV',
            homeScore: 3,
            awayScore: 1,
            status: 'Final',
            time: 'Apr 12 · 23:30',
            title: 'Man City 3-1 Liverpool — Haaland brace, Salah consolation',
            thumbnailLabel: 'Replay thumbnail',
            comments: ['Haaland is unreal', 'Liverpool collapsed in 2nd half', 'Title race is over']
          },
          {
            league: 'La Liga R30',
            homeTeam: 'Real Madrid',
            awayTeam: 'Barcelona',
            homeShort: 'RMA',
            awayShort: 'BAR',
            homeScore: 2,
            awayScore: 2,
            status: 'Final',
            time: 'Apr 13 · 03:00',
            title: 'El Clasico ends 2-2 — Vinícius equaliser',
            thumbnailLabel: 'Replay thumbnail',
            comments: ['El Clasico never disappoints', 'Vinícius is the real deal', 'Barca defence shaky']
          },
          {
            league: 'NBA Playoffs',
            homeTeam: 'Lakers',
            awayTeam: 'Warriors',
            homeShort: 'LAL',
            awayShort: 'GSW',
            homeScore: 112,
            awayScore: 108,
            status: 'Final',
            time: 'Apr 14 · 10:30',
            title: 'Lakers edge Warriors 112-108 — LeBron triple-double',
            thumbnailLabel: 'Replay thumbnail',
            comments: ['Bron still got it', 'Curry so close', 'Lakers championship?']
          },
          {
            league: 'UCL QF',
            homeTeam: 'Bayern',
            awayTeam: 'Arsenal',
            homeShort: 'BAY',
            awayShort: 'ARS',
            homeScore: 1,
            awayScore: 0,
            status: 'Final',
            time: 'Apr 15 · 03:00',
            title: 'Bayern 1-0 Arsenal — Kane header decides it',
            thumbnailLabel: 'Replay thumbnail',
            comments: ['Tough luck Arsenal', 'Kane is big-game player', 'Second leg still open']
          }
        ]
      }
    },

    matrix: {
      title: 'Terminal Support',
      subtitle: 'Replay Shield works on major browsers across major platforms',
      headers: ['Chrome', 'Edge', 'Firefox'],
      platforms: ['Windows / macOS', 'Android', 'iOS / iPadOS', 'HarmonyOS'],
      rows: [
        ['Yes', 'Yes', 'Yes'],
        ['Unsupported', 'Yes', 'Yes'],
        ['Unsupported', 'Unsupported', 'Unsupported'],
        ['Unsupported', 'Yes', 'Yes']
      ],
      legend: {
        yes: 'Supported',
        limited: 'Partial',
        no: 'Unsupported'
      }
    },

    faq: {
      title: 'FAQ',
      subtitle: 'Common questions about Replay Shield',
      items: [
        {
          q: 'Why are some scores still visible after enabling?',
          a: 'The website may have updated its layout recently. Reach us through the feedback link at the bottom of the page — we will follow up.'
        },
        {
          q: 'Will this affect other website functionality?',
          a: 'No. The extension only hides spoiler-related elements (scores, titles, thumbnails, comments). Video playback and other interactions remain unaffected.'
        },
        {
          q: 'Does it cover basketball too?',
          a: 'Yes. Replay Shield ships football and basketball match navigation (NBA, CBA, and more), and spoiler hiding works the same for both — switch anytime in the console sidebar.'
        },
        {
          q: 'How can I temporarily view scores while the extension is on?',
          a: 'Disable the extension temporarily and refresh the page.'
        },
        {
          q: 'Why can\'t I find the extension entry on mobile (iOS)?',
          a: 'iOS browsers are restricted by Apple\'s WebKit. Extension support is limited. For the full experience, use a desktop browser.'
        },
        {
          q: 'Is "Cannot verify extension source" normal after local install?',
          a: 'Yes — it\'s a browser safety measure. Choose "Keep" to continue.'
        },
        {
          q: 'Will uninstalling the extension affect my saved data?',
          a: 'No. Uninstalling only removes the extension itself, not any browser data.'
        }
      ]
    },

    meta: {
      title: 'Replay Shield — Watch replays like live matches',
      description: 'Hide sports video scores and spoilers. Works on Chrome, Edge, and Firefox.'
    }
  },

  // How-to guide + console feature reference. Strictly distinct from the legal
  // "usage notice" at /terms#specific-features — see CONTEXT.md "使用指南 vs 使用说明".
  // Copy must be actionable and honest; no filler.
  guide: {
    eyebrow: 'GUIDE · HOW TO USE',
    title: 'How to Use',
    subtitle: 'Installed? Start here: three steps into the console, then a tour of every feature it offers.',
    intro: 'Replay Shield\'s Match Navigation and viewing settings all live in the management console: click the Replay Shield icon in your toolbar, then click "Open management console" in the popup.',
    installFirst: 'Not installed yet? See the install guide',

    quick: {
      title: 'Three steps to start watching',
      mobileNote: 'Mobile (Android / HarmonyOS): after installing in Edge or Firefox, tap Replay Shield in the browser\'s extensions menu → "Open management console". iOS is not supported.',
      steps: [
        {
          title: 'Install the extension',
          desc: 'One-click install from the Chrome / Edge / Firefox store, or a manual .crx install.'
        },
        {
          title: 'Enter the management console',
          desc: 'Click the Replay Shield icon in your toolbar, then click "Open management console" in the popup.'
        },
        {
          title: 'Watch via Match Navigation',
          desc: 'Pick a football or basketball match and click through to watch. Scores, titles, and thumbnails are hidden automatically.'
        }
      ]
    },

    nav: {
      title: 'Match Navigation',
      intro: 'The console\'s Match Navigation splits by "sport preference" into football and basketball, with identical structure on both sides:',
      items: [
        {
          title: 'Football / basketball switch',
          desc: 'Switch between football and basketball under the "Sports" group in the sidebar; both sides list finished matches with daily-updated replays and highlights.'
        },
        {
          title: 'Channels & league filters',
          desc: 'Use the top channel selector to switch international / domestic sources, and league chips to filter horizontally — find the match you want fast.'
        },
        {
          title: 'Video sections',
          desc: 'Each match groups "Full replay", "Highlights" and more, tagged with source platform and commentator — one click to play.'
        },
        {
          title: 'Favorites',
          desc: 'Once signed in, favorite a whole match and find it later under "Favorites" in the sidebar.'
        }
      ]
    },

    settings: {
      title: 'Viewing settings',
      intro: 'Open "Settings" in the console sidebar and tune things to your liking (some items lock when signed out or without an active entitlement):',
      groups: [
        {
          title: 'Features',
          items: [
            'Enable extension: master switch — turning it off pauses everything',
            'Overlay: 0–10 s, blocks spoiler flashes right when a page opens',
            'Sport preference: all / football / basketball',
            'Desktop site: load supported sites in desktop layout on phones (manual toggle)'
          ]
        },
        {
          title: 'Playback',
          items: [
            'Intro cover panel: 0–60 s, covers the opening frames to hide score flashes',
            'Hide end time / hide progress bar: prevents time-based outcome guessing',
            'Seek panel: six steps from 10 seconds to 30 minutes',
            'Fullscreen lock: extra protection while watching fullscreen'
          ]
        },
        {
          title: 'Appearance',
          items: [
            'Palettes: Neon Cyan / Terminal Green / Electric Blue / Phantom Purple / Sunset Gold / Dawn White',
            'Interface language: 简体中文 / 繁體中文 / English'
          ]
        }
      ]
    },

    entitlement: {
      title: 'Free vs Pro membership',
      desc: 'Third-party match navigation and match browsing (football / basketball listings, progressive folding, commentator tags) is free for everyone; domestic & international match navigation and full protection — spoiler hiding, loading overlay, intro cover, progress bar protection — are Pro membership capabilities. New users get a Pro trial on sign-up.',
      cta: 'View Pro membership plans'
    },

    scope: 'Friendly reminder: Replay Shield only hides spoilers on pages opened from the console\'s Match Navigation — pages opened any other way are not protected.'
  },

  footer: {
    copyright: '© 2026 Replay Shield',
    brandDesc: '@:product.longIntro',
    sections: {
      product: 'Product',
      legal: 'Legal',
      social: 'Follow Us'
    },
    links: {
      membership: 'Pro membership',
      features: 'Features',
      install: 'Install',
      guide: 'Guide',
      matrix: 'Terminal Support',
      download: 'Download',
      about: 'About Us',
      faq: 'FAQ',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      refund: 'Refund Policy',
      invite: 'Invite Rules',
    },
    social: {
      wechatGzh: {
        title: 'WeChat Official Account',
        hint: 'Search 膨胀的球 to follow'
      },
      wechatGroup: {
        title: 'Feedback Group',
        hint: 'User feedback and chat'
      }
    }
  },

  common: {
    back: 'Back',
    learnMore: 'Learn more',
    getStarted: 'Get started',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    close: 'Close'
  },

  login: {
    title: 'Sign in',
    email: 'Email',
    emailPlaceholder: 'name\\@example.com',
    nickname: 'Nickname (optional)',
    nicknamePlaceholder: 'How should we call you?',
    password: 'Password',
    newPassword: 'New password',
    passwordPlaceholder: '8-20 chars, incl. upper, lower & digit',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Enter password again',
    passwordMismatch: 'Passwords do not match',
    passwordIncorrect: 'Incorrect password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    passwordStrength: 'Password strength',
    passwordWeak: 'Weak',
    passwordMedium: 'Medium',
    passwordStrong: 'Strong',
    code: 'Verification code',
    codePlaceholder: '6 digits',
    sendCode: 'Send code',
    codeSent: 'Verification code sent, please check your inbox',
    resetSuccess: 'Password reset. Please sign in with your new password',
    sessionExpired: 'Your session has expired. Please sign in again',
    kicked: 'Your account has been signed out on another device. Please sign in again',
    submit: 'Submit',
    submitError: 'Operation failed, please try again later',
    privacyPolicy: 'Privacy Policy',
    callbackTitle: 'Signed in',
    callbackReturning: 'Returning to Replay Shield…',
    callbackCountdown: 'Returning to Replay Shield automatically in {n}s',
    callbackGoAccount: 'Go to My Account',
    callbackOpenConsole: 'Open YFB console',
    resumeTitle: 'Already signed in on the website',
    resumeContinue: 'Continue with this account',
    resumeSwitch: 'Use another account',
    callbackReturnNow: 'Return to Replay Shield now',
    callbackWeakHint: 'If it doesn\'t return shortly, click the Replay Shield icon in the toolbar to check your login status',
    rememberAccount: 'Remember account',
    rememberPassword: 'Remember password',
    agreePrefix: 'I have read and agree to',
    terms: 'Terms of Service',
    agreeAnd: ' and ',
    agreeRequired: 'Please read and agree to the Terms of Service and Privacy Policy first',
    sliderTitle: 'Security verification',
    sliderHint: 'Drag the slider to complete the puzzle',
    sliderLoading: 'Loading…',
    sliderFail: 'Verification failed, please try again',
    sliderRefresh: 'Refresh',
    withQQ: 'Sign in with QQ',
    withDouyin: 'Sign in with Douyin',
    withGoogle: 'Sign in with Google',
    backToLogin: 'Back to sign in',
    forgotLink: 'Forgot password?',
    goLogin: 'Have an account? Sign in',
    goRegister: 'No account? Sign up',
    signIn: 'Sign in',
    signUp: 'Sign up',
    oauthDivider: 'Or sign in with a third-party account',
    oauthError: 'Third-party sign-in failed. Please retry or use email & password'
  },

  cashier: {
    title: 'Complete payment',
    chooseChannel: 'Choose a payment method',
    payWechat: 'WeChat Pay',
    wechatDesc: 'Scan the QR code',
    payAlipay: 'Alipay',
    alipayDesc: 'Opens Alipay checkout',
    days: '{days} days',
    wechatTip: 'Scan the QR code with WeChat to pay',
    alipayTip: 'Open Alipay to complete the payment; this page confirms the result automatically.',
    alipayGo: 'Pay with Alipay',
    wechatGo: 'Pay with WeChat',
    wechatGoTip: 'A payment QR code will be shown; scan it with WeChat to pay',
    initiating: 'Processing…',
    quoteRetry: 'Retry quote',
    originalPrice: 'Original price',
    reopen: 'Reopen payment page',
    awaitingCheck: 'After you finish paying on the payment page, the result will be confirmed automatically',
    pollChecking: 'Confirming your payment…',
    staleCheck: 'Confirmation timed out — refresh and keep waiting, or resume later from your account page',
    refreshStatus: 'Done paying? Refresh status',
    timeoutMsg: 'Not confirmed for a long time — rebuild the QR code or reopen the payment page',
    refreshQr: 'Rebuild QR code',
    doneTitle: 'Payment successful',
    doneDesc: 'Your subscription is active. The browser extension will unlock Pro membership automatically.',
    doneCta: 'Done',
    expiredTitle: 'Order not completed',
    closedMsg: 'This order was closed. Place a new order if you still want to subscribe.',
    okay: 'Got it',
    discountTitle: 'Choose a discount',
    discountDefault: 'Best offer (default)',
    discountRecommend: 'Recommended',
    discountSave: 'Save ¥{amount}',
    discount: {
      activity: 'Limited-time offer',
      inviteFirst: 'Invited first order',
      firstOrder: 'First-order discount',
      renewal: 'Renewal discount',
      noDiscount: 'No discount'
    },
    inviteBindDesc: 'Confirm binding your invite relationship with {name} ({account})? This lasts forever and can only be done once.',
    inviteAccept: 'Confirm & bind',
    inviteDecline: 'Don\'t bind',
    consentNote: 'By paying you agree to the',
    consentTerms: 'User Agreement',
    consentAnd: 'and',
    consentRefund: 'Refund Policy'
  },
  account: {
    title: 'My Account',
    notLoggedIn: 'You are not signed in',
    sections: {
      benefits: 'My Access',
      invites: 'My Invites',
      orders: 'Orders',
      profile: 'Profile'
    },
    benefitsDesc: 'View your Pro membership details, redemption history and access records',
    invitesDesc: 'View your invite code and rewards from your invites',
    profileDesc: 'Manage your nickname',
    ordersDesc: 'Track orders and access details; special-case refunds require support verification first',
    nickname: 'Nickname',
    email: 'Email',
    edit: 'Edit',
    editNickname: 'Edit nickname',
    notBound: 'Not linked',
    bindEmail: 'Link email',
    passwordSet: 'Set',
    passwordNotSet: 'Not set',
    password: 'Password',
    changePassword: 'Change password',
    oldPassword: 'Current password',
    newPassword: 'New password',
    deleteAccount: 'Delete account',
    deleteHint: 'Account data will be permanently deleted and cannot be recovered.',
    deleteConfirmPassword: 'Enter password to confirm',
    deleteConfirm: 'I understand the consequences and confirm deletion',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    saved: 'Saved',
    emailSaved: 'Email updated',
    emailBoundSaved: 'Email linked',
    passwordSaved: 'Password changed, please sign in again',
    samePassword: 'New password must differ from the current one',
    logout: 'Sign out',
    logoutConfirm: 'Sign out? This will also sign you out of the extension.',
    emailVerified: 'Verified',
    operationError: 'Operation failed, please try again later',
    // Third-party account binding (ADR-0041)
    loginMethodsTitle: 'Sign-in methods',
    oauthHint: 'Sign in with the linked provider to access this account',
    oauthBind: 'Link',
    oauthBoundLabel: 'Linked',
    oauthBound: '{provider} linked successfully',
    oauthBindError: 'Link failed, please try again',
    noEmailHint: 'Link an email and set a password to sign in with email and recover your account',
    setPassword: 'Set password',
    setPasswordHint: 'You signed in with a third-party provider and have no password yet; set one to sign in with email & password',
    subscriptionStatus: 'Access',
    subscriptionNone: 'No active access',
    subscriptionTrial: 'Trial · {days} days left',
    subscriptionActive: 'Pro membership · until {date}',
    subscriptionExpired: 'Pro membership ended on {date}',
    subscribeNow: 'Get Pro membership',
    hero: {
      daysLeft: '{days} days left',
      expiresAt: 'expires {date}',
      daysLeftLabel: 'Days left',
      expiresAtLabel: 'Expires',
      daysUnit: 'days'
    },
    inviteCode: 'My invite code',
    inviteLink: 'Invite link',
    inviteHint: 'Friends who register or subscribe with your code get mutual rewards',
    invitesEmpty: 'Unable to load your invite code, please try again later',
    copyInviteLink: 'Copy invite link',
    copied: 'Copied',
    memberBadge: {
      pro: 'PRO',
      trial: 'Trial'
    },
    rewards: {
      title: 'Invite records',
      total: '{days} days earned in total',
      empty: 'No invite records yet. You earn reward days when a friend registers with your code, and more when a friend gets Pro membership for the first time',
      loadMore: 'Load more',
      retracted: 'Retracted',
      subscribeNode: 'First subscription',
      registerNode: 'Registration'
    },
    recordsTitle: 'Access history',
    recordsEmpty: 'No access history yet',
    recordTrial: 'Trial',
    recordSubscribe: 'Subscription',
    recordInviteReward: 'Invite reward',
    recordRedeem: 'Redeem code',
    recordAdjust: 'Manual adjustment',
    recordContribute: 'Commentary reward',
    recordRecommend: 'Recommendation reward',
    recordAdvice: 'Feedback compensation',
    recordDays: 'Extended {days} days',
    recordDaysDeduct: 'Deducted {days} days',
    recordAt: 'until {date}',
    ordersTitle: 'Orders',
    ordersEmpty: 'No orders yet',
    ordersEmptyDesc: 'Payment and refund records will appear here once you get a Pro membership',
    ordersLoadMore: 'Load more orders',
    // Discount item display names (backend sends keys only; labels localized here; best-of applies at most one)
    discount: {
      activity: 'Campaign discount',
      inviteFirst: 'Invited first-order discount',
      firstOrder: 'First-order discount',
      renewal: 'Renewal discount',
      adminAdjust: 'Admin price adjustment'
    },
    plan: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    },
    order: {
      pending: 'Pending payment',
      paid: 'Paid',
      refundRequested: 'Refund in progress',
      refunded: 'Refunded',
      closed: 'Cancelled',
      payNow: 'Pay now',
      cancel: 'Cancel order',
      cancelConfirm: 'Cancel this pending order?',
      cancelled: 'Order cancelled',
      paySucceeded: 'Payment successful, access activated',
      payPending: 'Order created, please complete payment in your order',
      pendingHint: 'Pending orders are valid for {minutes} minutes and close automatically',
      pendingExpires: 'Pending · closes in {minutes} min',
      emptyCta: 'Get Pro membership',
      applyRefund: 'Request refund',
      refundPrompt: 'Reason for refund (optional, for improvement only):',
      refundSubmitted: 'Refund request submitted',
      cancelManual: 'Cancelled by user',
      cancelTimeout: 'Closed for timeout (unpaid)',
      cancelSuperseded: 'Superseded by a new order'
    },
    channel: {
      wechat: 'WeChat Pay',
      alipay: 'Alipay',
      mock: 'Mock payment'
    },
    cancelDialog: {
      title: 'Cancel order',
      confirm: 'Cancel this pending order? Create a new order to subscribe again.'
    },
    refundDialog: {
      title: 'Request refund',
      note: 'This is a virtual digital product. Refunds are settled pro-rata by used days; the activation day counts as one used day, so there is no 100% refund.',
      breakdownTitle: 'Refund breakdown',
      paidAt: 'Activation date',
      used: 'Used (incl. today)',
      remaining: 'Days left',
      total: 'Plan days',
      paidAmount: 'Amount paid',
      refundAmount: 'Refund this time',
      refundAmountTip: 'Refund = amount paid × days left ÷ plan days (rounded down)',
      confirmNote: 'Refund is executed immediately to the original payment method. Access is settled by remaining days, the inviter reward is fully retracted, and your first-order eligibility is not restored.',
      discountLossNote: 'After a successful refund, discounts used by this order (such as first-order or invited-first discounts) are not returned, and future subscriptions may no longer enjoy the first-order discount.',
      agreePolicy: 'I have read and agree to the',
      policy: 'Refund Policy',
      agreeRequired: 'Please tick that you have read the Refund Policy',
      reason: 'If you are unsatisfied with the product in any way, tell us so we can do better (optional)',
      submit: 'Confirm refund',
      notEligible: 'This order has no refundable amount left'
    },
    orderDetail: {
      title: 'Order details',
      orderNo: 'Order No. {no}',
      amount: 'Amount breakdown',
      original: 'Plan price',
      discount: 'Discount',
      channel: 'Payment method',
      inviter: 'Invited by {name}',
      payable: 'Amount paid',
      timeline: 'Progress',
      created: 'Order created',
      paidAt: 'Payment completed',
      entitledUntil: 'Access until',
      refundRequestedAt: 'Refund requested',
      refundedAt: 'Refund completed',
      closedAt: 'Order closed',
      refundReason: 'Refund reason',
      refundReview: 'Review note',
      refundPolicy: 'Refund Policy',
      resubscribe: 'Subscribe again'
    }
  },

  subscribe: {
    hero: {
      title: 'Pricing',
      subtitle: 'The free tier provides third-party match navigation; a Pro membership unlocks domestic & international match navigation and full spoiler protection. New users get a {days}-day Pro trial on registration.',
      ctaLogin: 'Sign in'
    },
    chip: {
      invited: 'Invited: {off} on first order',
      firstOrder: 'First order {off}',
      renewal: '{off} on renewals while active',
      default: 'Subscribe to unlock full protection'
    },
    campaign: {
      tag: 'Limited-time',
      countdown: 'Ends in',
      days: 'd',
      deadline: 'Ends',
      hint: 'This is a limited-time campaign price; renewals are priced at the renewal discount'
    },
    scenes: {
      title: 'Watch replays without spoilers',
      subtitle: 'Replay Shield hides scores and key events on the video page in real time, so you can rewatch any match safely',
      demo: {
        without: 'No protection',
        with: 'Pro active',
        teams: 'Arsenal vs Chelsea · Premier League MD30',
        score: '2 - 1',
        scorer: 'Saka scores in the 78th',
        hidden: 'Hidden'
      },
      s1: {
        title: 'Spoiler-free replays',
        desc: 'Scores, scorers and key events are hidden automatically while you watch — revealed only when you get there'
      },
      s2: {
        title: 'Key moments, unspoiled',
        desc: 'Revisit goals, red cards and penalties moment by moment without the result being spoiled'
      },
      s3: {
        title: 'Season archive',
        desc: 'Save full matches to your archive — never expires, rewatch anytime'
      }
    },
    badge: {
      activity: 'Campaign',
      inviteFirst: 'Invited first order',
      firstOrder: 'First order',
      renewal: 'Renewal',
      noDiscount: 'No discount'
    },
    offZhe: '{value} 折',
    offPercent: '{percent}% off',
    status: {
      trial: 'Trial · {days} days left',
      subscribed: 'Pro membership · until {date}',
      expired: 'Pro membership ended on {date}',
      trialExpired: 'Trial ended on {date}',
      none: 'Get Pro membership for full protection',
      refresh: 'Refresh entitlement status'
    },
    compare: {
      title: 'Free vs Pro membership',
      subtitle: 'Sign up for the free tier (third-party match navigation); a Pro membership unlocks domestic & international match navigation and full spoiler protection',
      headerFree: 'Free',
      headerSub: 'Pro',
      infoLabel: 'feature details',
      rows: [
        {
          label: 'Football third-party navigation',
          group: 'free',
          free: true,
          sub: true,
          info: 'Curated football replays and highlights across third-party platforms including Zhibo8, Migu Video, iQIYI, CCTV Sports and Yangshipin — one click to watch on the source platform.'
        },
        {
          label: 'Basketball third-party navigation',
          group: 'free',
          free: true,
          sub: true,
          info: 'Curated basketball replays and highlights across third-party platforms including Zhibo8, Migu Video, iQIYI, CCTV Sports and Yangshipin — one click to watch on the source platform.'
        },
        {
          label: 'Progressive navigation',
          group: 'free',
          free: true,
          sub: true,
          info: 'For matches that may go to extra time or a penalty shootout (e.g. football / basketball), extra-time and shootout videos stay collapsed by default. Watch the full regulation match first, then expand the overtime or shootout only if you want — so you never learn the result ahead of time.'
        },
        {
          label: 'Commentator tags',
          group: 'free',
          free: true,
          sub: true,
          info: 'Replays from each source site are tagged with commentator info, so you can pick your favorite commentator to watch.'
        },
        {
          label: 'Upcoming fixtures',
          group: 'free',
          free: true,
          sub: true,
          info: 'A two-week fixtures timeline: see which matches are coming up, when they kick off and who plays whom, so you can plan your viewing.'
        },
        {
          label: 'Football domestic & international navigation',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Top leagues like the Premier League, La Liga, UCL and the World Cup, plus domestic and community tournaments. Replays & highlights of finished matches, updated daily — watch in one click.'
        },
        {
          label: 'Basketball domestic & international navigation',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Major basketball events including the NBA and CBA. Replays & highlights of finished matches, updated daily — watch in one click.'
        },
        {
          label: 'Spoiler hiding',
          group: 'pro',
          free: false,
          sub: true,
          core: true,
          info: 'The core feature: hides titles, scores, covers, descriptions and comments across the page, so nothing spoils your replay.'
        },
        {
          label: 'Loading overlay',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Shows an overlay while the page loads, preventing scores and spoilers from flashing on the first frame.'
        },
        {
          label: 'Intro cover panel',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Covers the opening of highlights or replays for a set number of seconds, so on-screen scores never spoil the start.'
        },
        {
          label: 'Progress bar protection',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Hides the progress bar and end time, so you can never infer the result from progress or duration.'
        },
        {
          label: 'Seek floating buttons',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Floating rewind / fast-forward buttons with a customizable step, so you control the pace of playback.'
        },
        {
          label: 'Auto settings sync',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Your settings and preferences sync automatically after sign-in, so switching devices is seamless — no reconfiguring.'
        },
        {
          label: 'Fullscreen lock',
          group: 'pro',
          free: false,
          sub: true,
          info: 'In fullscreen playback a lock button appears; tap to lock the screen against accidental clicks, tap again to unlock. Exiting fullscreen unlocks automatically.'
        },
        {
          label: 'Favorites',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Bookmark whole matches to rewatch anytime, football and basketball alike; favorites live in the cloud forever, even past the daily update window.'
        },
        {
          label: 'Following (competitions & teams)',
          group: 'pro',
          free: false,
          sub: true,
          info: 'Follow the competitions and teams you care about; the "Following" tab on the match page shows only their matches, and can be narrowed to "My teams". Independent of Favorites: following decides which matches you see, favoriting is for rewatching whole matches.'
        }
      ]
    },
    plans: {
      title: 'Choose a Pro membership plan',
      subtitle: 'One upgrade unlocks all protection features.',
      bestValue: 'Best value',
      days: '{days} days of access',
      perMonth: '≈ ¥{price}/mo',
      save: 'Save ¥{amount} vs monthly',
      originalPrice: 'Orig.',
      guestNote: 'Signed-out prices assume new-user eligibility; sign in to see your actual price',
      subscribe: 'Get Pro membership',
      renew: 'Renew Pro membership',
      processing: 'Processing…'
    },
    order: {
      success: 'Upgraded! Access extended {days} days, until {date}',
      pending: 'Order created ({orderNo}), please complete payment in your orders'
    },
    resume: {
      pending: 'You have an unfinished payment',
      continue: 'Continue payment',
      alreadyPaid: 'This order was already paid. Your benefits are active',
      expired: 'That pending order has expired or was replaced. Please place a new order.'
    },
    consent: {
      note: 'By continuing you acknowledge that you have read and agree to',
      terms: 'the User Agreement',
      and: 'and',
      refund: 'the Refund Policy',
      disclaimer: 'This tool only provides local hiding on supported video pages; full coverage of every page at every moment is not guaranteed.'
    },
    notice: {
      title: 'Before you subscribe',
      items: [
        'Access is granted immediately after payment and extends from your current expiry.',
        'This is a virtual digital product and no-reason full refunds are not offered; for special cases, contact feedback — verified cases are refunded pro-rata by used days.',
        'This tool only provides local hiding on supported video pages; full coverage of every page at every moment is not guaranteed.',
        'Trial, redeem-code, invite-reward and gifted access are not refundable.'
      ],
      gotIt: 'Got it'
    },
    invite: {
      title: 'Invite friends, reward both sides',
      subtitle: 'When a friend registers or subscribes through your invite link, both of you get real rewards.',
      myCode: 'My invite code',
      shareHint: 'Send your invite link to a friend: registering within 24 hours of clicking binds the invite, and both sides benefit instantly',
      copy: 'Copy link',
      copied: 'Invite link copied',
      invitedDesc: 'Invited you to get Pro membership with {off} on your first order',
      clear: 'Clear',
      clearHint: 'Clears the saved invite code — non-invited pricing applies',
      stats: 'Invited {count} · earned {days} days in total',
      friendTitle: 'For your friend',
      friendRule1: 'When your friend gets their FIRST Pro membership with your code, they get {off} on that first order',
      meTitle: 'For you',
      meRule1: 'When a friend registers with your code, you instantly earn +{days} days',
      meRule2: 'When a friend gets their FIRST Pro membership plan, you earn {percent}% of that plan\'s duration (first purchase only)',
      rulesLink: 'Read the full invite rules'
    },
    redeem: {
      title: 'Redeem code',
      subtitle: 'Have a code? Enter it to redeem the matching Pro membership days. Each account can redeem it once.',
      placeholder: 'Enter redeem code',
      submit: 'Redeem',
      processing: 'Redeeming…',
      success: 'Redeemed! Access extended {days} days, until {date}',
      codeRequired: 'Please enter a redeem code'
    },
    faq: {
      title: 'FAQ',
      items: [
        { q: 'Why isn\'t WeChat sign-in or WeChat Pay supported?', a: 'This first release only supports Alipay; WeChat sign-in and WeChat Pay are not connected yet. Both will be added in a later version and announced on the sign-in and pricing pages. For now, please pay with Alipay.' },
        { q: 'How do I get a redeem code?', a: 'Redeem codes come mainly from our WeChat official account (膨胀的球), the official feedback group, compensation for product issues and promotions. Enter one in the Redeem section on the pricing page: each code works once and extends your access right away. QR codes for the account and the group are on the About page.' },
        { q: 'What happens to my invite rewards if a friend refunds?', a: 'When a friend refunds their first subscription order, the subscription reward you earned from them is fully retracted: your access history and invite history are marked "Retracted" and your expiry date is recalculated. The registration reward is not retracted.' },
        { q: 'Why does the price change after I sign in?', a: 'While signed out, plans show an estimated price that assumes a new-user profile. After you sign in, the system reprices based on your actual eligibility (first subscription, invited status, current campaign), so the price can be lower or return to the list price. The amount shown at checkout is what you pay.' },
        { q: 'What happens if I change the discount at checkout?', a: 'Checkout selects the best offer for you by default. You can switch to another discount or choose no discount. You pay exactly the amount of the option you pick, and the order records the same choice. Whichever you pick, that order still counts as your first subscription.' },
        { q: 'Do the trial and redeem codes count as my first subscription?', a: 'No. Trial days, redeem codes, invite rewards and gifted access involve no payment, so they never consume your first-order eligibility. Only a subscription order you actually paid for counts as your first subscription.' },
        { q: 'I paid but my membership hasn\'t arrived. What now?', a: 'Payment results are reconciled automatically: checkout confirms the payment and extends your access for you, usually within seconds. If the page stays in the confirming state, choose Refresh status, or sign in again and check your account page. If it still has not arrived, send us the order number through the extension\'s Feedback form and we will sort it out.' },
        { q: 'Do I keep my membership if I switch computer or reinstall the browser?', a: 'Pro membership is tied to your account, not to a device. Install the extension in a new browser and sign in with the same account to get the protection and your remaining days back. Only the trial is guarded by both device and identity, one per device.' },
        { q: 'Does renewing early waste my remaining days?', a: 'No. New days are added after your current expiry date, so renewing early never clears your balance. Redeem codes and invite rewards stack the same way. Your expiry date is shown on your account page and on the pricing page.' }
      ]
    },
    errors: {
      loadPlans: 'Failed to load plans, please try again later',
      noChannel: 'Payment is temporarily unavailable, please try again later',
      generic: 'Operation failed, please try again later',
      copyFailed: 'Copy failed, please copy manually'
    },
    loading: 'Loading…'
  },

  validation: {
    emailRequired: 'Please enter your email',
    emailMax: 'Email must be at most 100 characters',
    emailFormat: 'Invalid email format',
    passwordRequired: 'Please enter your password',
    passwordFormat: 'Password: 8-20 chars incl. uppercase, lowercase & digit',
    passwordLength: '8-20 characters required',
    passwordLower: 'Must include a lowercase letter',
    passwordUpper: 'Must include an uppercase letter',
    passwordDigit: 'Must include a digit',
    passwordSpace: 'Must not contain spaces',
    codeRequired: 'Please enter the verification code',
    codeFormat: 'Verification code must be 6 digits',
    nameRequired: 'Please enter a nickname',
    nameMax: 'Nickname must be at most 30 characters'
  },

  install: {
    crumbs: 'Breadcrumb navigation',
    crumbsHome: 'Install',

    // Per-device install tabs (desktop Chrome/Edge/Firefox/other-Chromium + mobile Edge/Firefox + iOS card).
    // Each tab walks the full journey: install → pin → open the console → watch.
    // Screenshot slots follow docs/main/specs/assets.md.
    tabs: {
      title: 'Install Replay Shield',
      subtitle: 'Pick your device and browser, follow the steps, and start watching',
      calloutBadge: 'Desktop recommended',
      calloutTitle: 'Store installs auto-update — easiest path',
      groups: { desktop: 'Desktop', mobile: 'Mobile', ios: 'iOS / iPadOS' },
      tabLabels: {
        chrome: 'Chrome',
        chromeManual: 'Chrome · Manual',
        edge: 'Edge',
        firefox: 'Firefox',
        chromium: 'Other Chromium',
        edgeMobile: 'Edge · Mobile',
        firefoxMobile: 'Firefox · Mobile'
      },
      platformBadges: {
        desktop: 'Windows / macOS / Linux',
        android: 'Android / HarmonyOS'
      },
      recommended: 'Recommended',
      methodBadges: { store: 'From store', manual: 'Manual install' },
      intro: {
        chrome: 'Two ways to install: from the store (recommended — auto-updates) or manually if you cannot reach the store.',
        chromeManual: 'For when the Chrome Web Store is unreachable. Note: manually installed versions do not auto-update — check the site for new releases.',
        edge: 'Edge installs through the Edge Add-ons store.',
        firefox: 'Firefox installs through the Firefox Add-ons store.',
        chromium: 'Chromium-based browsers such as 360 Speed, QQ, Sogou, Liebao, Opera, and UC can load the .crx the same way as Chrome\'s manual install.',
        edgeMobile: 'On mobile only Edge and Firefox support extensions, and only store installs are supported — no manual installs.',
        firefoxMobile: 'On mobile only Edge and Firefox support extensions, and only store installs are supported. If Firefox is unavailable in your app store, use the APK link below.'
      },
      steps: {
        chrome: [
          'Open Chrome and visit the Chrome Web Store',
          'Type "Replay Shield" (隐分宝) in the search box',
          'Click "Add to Chrome" on the right side of the page',
          'Click "Add extension" in the confirmation dialog',
          'Once installed, the Replay Shield icon appears on Chrome\'s toolbar',
          'If the icon is tucked away, open the puzzle menu on the toolbar, find Replay Shield, and click the pin to keep it visible (recommended)',
          'Click the Replay Shield icon, then click "Open management console" in the popup',
          'In the console, pick a match from Match Navigation to start watching, or customize your viewing settings in the sidebar'
        ],
        chromeManual: [
          'Open Replay Shield\'s manual install page and download the .crx package (entry below)',
          'Click the browser menu icon, find "Extensions", and open the extensions management page',
          'Turn on the "Developer mode" toggle in the top-right corner',
          'Drag the downloaded .crx file from your folder straight into the extensions page',
          'Click "Add extension" in the confirmation dialog',
          'Once installed, open the puzzle menu, find Replay Shield, and pin it to the toolbar (recommended)',
          'Click the Replay Shield icon, then click "Open management console" in the popup',
          'In the console, pick a match from Match Navigation to start watching, or customize your viewing settings in the sidebar'
        ],
        edge: [
          'Open Edge and visit the Edge Add-ons store',
          'Type "Replay Shield" (隐分宝) in the search box',
          'Click the "Get" button',
          'Click "Add extension" in the confirmation dialog',
          'Once installed, the Replay Shield icon appears on Edge\'s toolbar',
          'Open the puzzle menu on the toolbar, find Replay Shield, and click the pin to keep it visible (recommended)',
          'Click the Replay Shield icon, then click "Open management console" in the popup',
          'In the console, pick a match from Match Navigation to start watching, or customize your viewing settings in the sidebar'
        ],
        firefox: [
          'Open Firefox\'s Add-ons and themes page',
          'Type "Replay Shield" (隐分宝) in the search box',
          'Click the "Add to Firefox" button',
          'Click "Add" in the confirmation dialog',
          'Once installed, check "Pin to toolbar" when prompted',
          'Click the Replay Shield icon, then click "Open management console" in the popup',
          'In the console, pick a match from Match Navigation to start watching, or customize your viewing settings in the sidebar'
        ],
        chromium: [],
        edgeMobile: [
          'Install the Edge browser from your app store',
          'Open Edge and tap the menu icon on the home page',
          'Tap "Extensions" in the menu to open the extensions manager',
          'Search for "Replay Shield" (隐分宝) and tap "Get"',
          'Tap "Add" in the popup — it now shows as installed',
          'Back on the home page, open the menu → "Extensions" again: Replay Shield is now listed',
          'Tap Replay Shield, then tap "Open console" in the popup to start watching',
          'You can also open the sidebar to customize your viewing settings'
        ],
        firefoxMobile: [
          'Install Firefox from your app store; if Google Play is unreachable, use the APK link below',
          'Open Firefox and tap the menu icon in the top-right corner',
          'Tap "Extensions" in the menu',
          'Scroll to the bottom of the extensions list and tap "Find more extensions" to open the add-ons store',
          'Type "Replay Shield" (隐分宝) in the store search box',
          'Open the detail page, tap "Add to Firefox", then "Add" in the popup — installed',
          'Important: after installing, open any web page first, then tap the menu → "Extensions" — Replay Shield now appears in the list',
          'Tap Replay Shield, then tap "Open management console" in the popup to start watching; the sidebar customizes your settings'
        ]
      },
      links: {
        manualGuideText: 'Open the manual install guide',
        manualGuideHint: 'Supported browsers + developer-mode drag-and-drop steps',
        apkText: 'Firefox APK download (official Mozilla server)',
        apkHref: 'https://ftp.mozilla.org/pub/fenix/releases/152.0.5/android/fenix-152.0.5-android-arm64-v8a/'
      },
      chromiumCta: 'View manual install guide',
      doneText: 'All install steps done — enjoy!',
      noticeTitle: 'Friendly reminder',
      notice: 'Spoilers are hidden only on the first page you open from Replay Shield\'s console. Pages opened from any other source cannot get spoiler hiding.',
      ios: {
        title: 'iOS / iPadOS not supported yet',
        reason: 'Apple restrictions prevent iOS browsers from loading extensions.',
        note: 'If a workable iOS approach matures later, we will announce it on the site right away.'
      },
      helpLine: 'Need help? Contact {email}'
    },

    store: {
      title: 'Install from store',
      subtitle: 'One-click install for your browser, with auto-updates',
      detectedBadge: 'Recommended',
      detectedCta: 'Open {browser} store',
      fallbackHint: 'Not {browser}? Pick another browser',
      fallbackLabel: 'Choose browser',
      backToInstall: 'Back to install methods',

      methodStore: 'One-click store install',

      browsers: {
        chrome: 'Chrome',
        edge: 'Edge',
        firefox: 'Firefox'
      },
      platforms: {
        desktop: 'Desktop',
        android: 'Android',
        ios: 'iOS',
        harmonyos: 'HarmonyOS'
      },
      unavailable: 'Not supported',
      useDesktop: 'Please use a desktop browser (Chrome, Edge, or Firefox) for the full experience.',
      backToStore: 'Back to install guide',

      chrome: {
        desktop: {
          title: 'Chrome Desktop Install',
          subtitle: 'Windows / macOS / Linux',
          storeSteps: [
            'Open Chrome browser',
            'Go to Chrome Web Store',
            'Search for "Replay Shield"',
            'Click "Add to Chrome"',
            'Confirm "Add extension" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        },
        android: {
          title: 'Chrome · Android',
          unavailableReason: 'Chrome on Android does not support extensions. We recommend Edge or Firefox instead.'
        },
        ios: {
          title: 'Chrome · iOS',
          unavailableReason: 'Chrome on iOS does not support extensions.'
        },
        harmonyos: {
          title: 'Chrome · HarmonyOS',
          unavailableReason: 'Chrome does not support extension installation on HarmonyOS. We recommend Edge or Firefox instead.'
        }
      },

      edge: {
        desktop: {
          title: 'Edge Desktop Install',
          subtitle: 'Windows / macOS / Linux',
          storeSteps: [
            'Open Edge browser',
            'Go to Edge Add-ons store',
            'Search for "Replay Shield"',
            'Click "Get"',
            'Confirm "Add extension" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        },
        android: {
          title: 'Edge · Android',
          storeSteps: [
            'Open Edge browser',
            'Go to Edge Add-ons store',
            'Search for "Replay Shield"',
            'Click "Get"',
            'Confirm "Add extension" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        },
        ios: {
          title: 'Edge · iOS',
          unavailableReason: 'Edge on iOS does not support extensions.'
        },
        harmonyos: {
          title: 'Edge · HarmonyOS',
          storeSteps: [
            'Open Edge browser',
            'Go to Edge Add-ons store',
            'Search for "Replay Shield"',
            'Click "Get"',
            'Confirm "Add extension" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        }
      },

      firefox: {
        desktop: {
          title: 'Firefox Desktop Install',
          subtitle: 'Windows / macOS / Linux',
          storeSteps: [
            'Open Firefox browser',
            'Go to Firefox Add-ons store',
            'Search for "Replay Shield"',
            'Click "Add to Firefox"',
            'Confirm "Add" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        },
        android: {
          title: 'Firefox · Android',
          storeSteps: [
            'Open Firefox browser',
            'Go to Firefox Add-ons store',
            'Search for "Replay Shield"',
            'Click "Add to Firefox"',
            'Confirm "Add" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        },
        ios: {
          title: 'Firefox · iOS',
          unavailableReason: 'Firefox on iOS does not support extensions.'
        },
        harmonyos: {
          title: 'Firefox · HarmonyOS',
          storeSteps: [
            'Open Firefox browser',
            'Go to Firefox Add-ons store',
            'Search for "Replay Shield"',
            'Click "Add to Firefox"',
            'Confirm "Add" in the popup, and grant the requested permissions',
            'After install, pin Replay Shield to your toolbar for quick access'
          ]
        }
      }
    },

    manual: {
      title: 'Manual install .crx',
      subtitle: 'Works on all Chromium-based browsers, including domestic browsers not listed in any store',
      platformSupport: {
        title: 'Platform support',
        items: [
          {
            mark: '✓',
            status: 'recommended',
            name: 'Desktop',
            hint: 'Recommended. Full support on Windows / macOS for the best experience.'
          },
          {
            mark: '△',
            status: 'partial',
            name: 'Android',
            hint: 'Partial. Use a browser that supports loading .crx files (e.g. Lemur Browser, Kiwi Browser, etc.).'
          },
          {
            mark: '✗',
            status: 'unsupported',
            name: 'iOS',
            hint: 'Not supported. Apple restrictions prevent browsers from installing extensions normally.'
          }
        ]
      },
      supportedTitle: 'Supported browsers',
      supportedBrowsers: [
        'Google Chrome',
        '360 Speed Browser',
        'QQ Browser',
        'Sogou High-Speed Browser',
        '360 Browser',
        'Liebao Browser',
        'Opera',
        'UC Browser'
      ],
      stepsTitle: 'Installation steps',
      steps: [
        'Download the .crx installer',
        'Click the browser menu icon and open the extensions manager (usually under Settings / Tools / Extensions)',
        'Turn on the "Developer mode" toggle in the top-right corner',
        'Drag the downloaded .crx file from your folder straight into the extensions page',
        'Click "Add / Install" in the popup, and grant the requested permissions',
        'After install, pin Replay Shield to your toolbar for quick access'
      ],
      downloadCta: 'Download .crx installer',
      backToInstall: 'Back to install guide',
      supportedNote: 'Desktop browsers only (Windows / macOS)',
      helpLine: 'Need help? Contact {email}'
    }
  },

  download: {
    title: 'Download Replay Shield',
    subtitle: 'We auto-detected your browser. Click the button below to download the installer.',
    detectingBrowser: 'Detecting browser...',
    directDownload: 'Direct download',
    fallbackHint: 'If not auto-detected, please select your browser manually',
    viewOnStore: 'View on store'
  },

  about: {
    title: 'About Replay Shield',
    intro: '@:product.aboutIntro',
    capabilitiesTitle: 'Core capabilities',
    sitesTitle: 'Supported sites',
    sitesHint: 'Works on {count} major sports video sites',
    communityTitle: 'Official account & feedback group',
    communityHint: 'Follow the account for updates; join the group for feedback',
    wechatGzh: {
      title: 'WeChat Account (膨胀的球)',
      hint: 'Search 膨胀的球 on WeChat to follow'
    },
    wechatGroup: {
      title: 'WeChat feedback group',
      hint: 'Scan the QR code to join user feedback and chat'
    },
    authorTitle: 'About the author',
    authorContent: 'Built and maintained by 膨胀 (SwellMan). What started as a personal tool to dodge replay spoilers has grown into a full extension covering the three major desktop browsers and a dozen-plus sports video sites. Hope it helps you recapture that first-time thrill of watching the game.',
    contactTitle: 'Contact',
    contactEmailLabel: 'Feedback email',
    legalTitle: 'Legal',
    legalLinks: {
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    }
  },

  // Supported site names (shared by Home / About; official English names per site)
  sites: {
    migu: { name: 'Migu Video' },
    iqiyi: { name: 'iQIYI' },
    bilibili: { name: 'Bilibili' },
    cctv: { name: 'CCTV' },
    yangshipin: { name: 'Yangshipin' },
    tencent: { name: 'Tencent Video' },
    douyin: { name: 'Douyin' },
    weibo: { name: 'Weibo' },
    xiaohongshu: { name: 'Xiaohongshu' }
  },

  privacy: {
    title: 'Privacy Policy',
    subtitle: 'Our commitment to your privacy',
    intro: 'We respect and protect the privacy of every user. This policy explains in plain language what information our product collects, why, how it is used, how it is protected, and what rights you have over your information.',
    principlesTitle: 'Core principles',
    principles: [
      {
        title: 'Local-first',
        desc: 'The core function — detecting and hiding spoiler information — runs entirely in your browser. Browsing content is never uploaded to our servers.'
      },
      {
        title: 'No tracking',
        desc: 'We do not track your browsing history, do not collect your browsing behavior, and do not build any form of user profile.'
      },
      {
        title: 'Zero ads',
        desc: 'No ad slots, no promotional partnerships, and no third-party tracking scripts; subscriptions only support ongoing development.'
      },
      {
        title: 'Minimum necessary',
        desc: 'We request only the browser permissions strictly required for core functionality. Any change to the permission set will be reflected in this policy.'
      }
    ],
    sections: {
      scope: {
        title: '1. Scope of this policy',
        content: 'This policy applies to the "Replay Shield" browser extension and this website at woofootball.site. Throughout this document, "the product" and "we" refer to the operator of the extension and the website. This policy does not apply to any third-party websites you visit through the extension — those sites have their own privacy policies, which you should review separately.'
      },
      whatWeCollect: {
        title: '2. What information we collect',
        items: [
          'Information you actively submit: when you contact us through channels such as "Feedback," we receive the content you choose to provide (e.g., email address, problem description, attached images).',
          'Extension preferences: the language, theme, and hiding-rule toggles you choose are stored locally in your browser and used only locally for product operation.',
          'Necessary security logs: when our endpoints receive a request, we record the minimum information required by industry practice (e.g., request time, necessary network identifiers) for security auditing, troubleshooting, and abuse prevention.',
          'Payment and order information: when you subscribe to Pro membership, we process what is necessary to complete your subscription order (e.g., order number, plan and amount, transaction voucher returned by the payment provider, refund reason and review records), used solely for order processing, financial reconciliation, refund review, and customer service.'
        ]
      },
      permissions: {
        title: '3. Browser permissions explained',
        intro: 'To provide core functionality, the extension needs to request certain browser permissions. Below we describe each by purpose — not by the raw permission name — so it is clearer what we can and cannot do with them.',
        items: [
          'Access the current page content: used to hide spoiler elements according to your rules when you visit supported sports video pages. This capability is engaged only when you actively interact with the extension or land on a supported page.',
          'Store preferences locally: saves your chosen language, theme, and rule toggles in your browser, where you can view and clear them at any time via the extension settings.',
          'Apply style rules on supported sports video sites: hiding rules are applied only against a maintained list of sites. The current list is available in the extension settings and is updated as the product evolves.',
          'Receive your active instructions: responds to actions such as clicking the extension icon, toggling settings, and submitting feedback.'
        ]
      },
      dataRetention: {
        title: '4. Data retention and deletion',
        items: [
          'Local preferences: stored in your browser until you uninstall the extension, clear extension data, or reset manually.',
          'Feedback: retained only for the period reasonably necessary to handle your request, after which it is deleted or anonymized.',
          'Security logs: retained only as long as necessary to safeguard the service, troubleshoot issues, and respond to security events, in line with industry practice and applicable law.',
          'If you would like us to delete information you have actively submitted, please contact us using the details at the end of this policy.'
        ]
      },
      userRights: {
        title: '5. Your rights over your personal information',
        intro: 'We respect the rights you have over your personal information. You may:',
        items: [
          'Query and obtain a copy of the personal information we hold about you',
          'Correct or supplement information that is inaccurate or incomplete',
          'Request deletion of relevant information where the conditions required by law are met',
          'Withdraw consent you previously provided',
          'Raise questions, complaints, or reports using the contact details at the end of this policy'
        ],
        closing: 'Because this product has no account system, the vast majority of your preference data lives on your local device. You can exercise the rights above directly through the extension settings or your browser\'s data management tools. For anything that requires our assistance, please contact us using the details below and we will respond within a reasonable timeframe.'
      },
      security: {
        title: '6. How we protect your information',
        items: [
          'We use industry-standard encrypted transport and signing mechanisms to protect communication between your browser and our services',
          'Local data is stored in the isolated areas provided by your browser, separate from ordinary web pages',
          'We continuously monitor industry security practices and update our protections as needed',
          'However, no system can guarantee absolute security. In the event of a security incident, we will notify you in a timely manner as required by law'
        ]
      },
      crossBorder: {
        title: '7. Cross-border transfers',
        content: 'Our service infrastructure is deployed in the People\'s Republic of China. By default, your information is processed within this region. If you use the product from outside this region, the necessary network communication will be conducted in accordance with applicable law. We do not actively transfer your personal information outside this region.'
      },
      children: {
        title: '8. Children\'s privacy',
        content: 'This product is not specifically designed for minors under the age of fourteen. Where minors use this product, it should be done with the consent of their guardian. We do not knowingly collect personal information from minors. If you, as a guardian, find that a minor has submitted personal information without consent, please contact us using the details at the end of this policy and we will assist you.'
      },
      thirdParty: {
        title: '9. Third-party links and services',
        content: 'Our product may contain links to third-party browser stores and other third-party sites, or may operate on supported third-party sports video sites. These third parties maintain their own privacy policies and terms. We do not control their practices. We recommend reviewing the relevant third-party policies before use.'
      },
      updates: {
        title: '10. Updates to this policy',
        content: 'We may update this policy as the product evolves and as laws and regulations change. For material changes, we will give advance notice through the in-extension "version updates" page and via announcements on this website. Continued use of the product after the changes take effect constitutes acceptance of the updated policy.'
      },
      contact: {
        title: '11. Contact us',
        content: 'If you have any questions about this policy or how we handle your personal information, or wish to exercise the rights granted by this policy, please contact us at the email below:'
      }
    }
  },

  terms: {
    title: 'Terms of Service',
    subtitle: 'Please read before using this product',
    intro: 'Welcome to "Replay Shield" and its official website (together, "the product"). These Terms form an agreement between you and the operator regarding your use of the product. Please read them carefully; by using the product, you confirm that you have read, understood, and agreed to these Terms.',
    sections: {
      service: {
        title: '1. The service',
        content: 'The product is a browser extension that locally hides spoiler information — such as scores, titles, thumbnails, and comments — on sports replay and highlight pages. The website provides introduction, installation, and download entry points for the extension. The service is free to use (core features) plus a paid Pro subscription: new users get a Pro trial on registration, with full functionality during the trial.'
      },
      subscriptionPay: {
        title: '2. Subscriptions, payment, and refunds',
        intro: 'This section covers pricing, payment, access delivery, and refunds for the Pro membership subscription. The Refund Policy is the detailed version of this section; where they conflict, the Refund Policy prevails.',
        items: [
          'Pricing and fees: plan prices are as shown on the pricing page. During a campaign, the discounted price applies and reverts after the campaign ends. Friends who register through your invite link and get bound to you enjoy the invited first-order discount; when several discounts apply, you can pick one in the checkout (the best one is selected by default).',
          'Payment providers: payment is provided by third-party providers (e.g. Alipay) and may carry a small processing fee.',
          'Access delivery: access is granted only after successful payment and extends from the greater of your current expiry and the current time; no access is granted before payment.',
          'Refunds: this is a virtual digital product and no active refund entry is offered by default. For special cases (product issues, etc.), once the operator verifies and enables refund permission for your account, refunds are settled pro-rata by used days (used days include the activation day; there is no 100% refund). After a refund, the corresponding invite reward is fully retracted and first-order eligibility is not restored. Trial, redeem-code, invite-reward, and gifted access are not refundable. See the Refund Policy for details.',
          'Scope and limitations: the product is provided "as is" and only responsible for local hiding on supported pages; it does not guarantee complete coverage of every page at every moment. The operator bears no liability beyond the amount actually paid for hiding failures caused by third-party site redesigns or page-structure changes.',
          'Service termination: if the operator discontinues the subscription service, the operator will refund the unconsumed paid time in proportion.'
        ]
      },
      license: {
        title: '3. License',
        content: 'The operator grants you a non-exclusive, non-transferable, revocable right to use the product. You may install and use the extension on your personal devices, subject to the laws and regulations of your jurisdiction. The operator reserves the right to suspend or terminate part or all of the service at any time.'
      },
      intellectualProperty: {
        title: '4. Intellectual property',
        items: [
          'The product\'s name, logo, interface design, icons, and copy are protected by intellectual property rights and may not be used without the operator\'s written authorization',
          'Open-source components used in the product and their license information are disclosed in the in-extension "About" page',
          'Names, logos, and domains of any third-party sites the product operates against are trademarks of their respective owners. The operator has no endorsement, licensing, or partnership relationship with such third parties'
        ]
      },
      restrictions: {
        title: '5. Restrictions',
        items: [
          'No reverse engineering, decompilation, disassembly, or attempts to extract source code',
          'No modification, derivative works, redistribution, or commercial resale of the product',
          'No use of the product for any activity violating local laws or regulations',
          'No use of the product to bypass access controls, paywalls, or subscription verification on any third-party site',
          'No use of the product to harass, defraud, or distribute unlawful or harmful content toward any party'
        ]
      },
      specificFeatures: {
        title: '6. Specific feature notice',
        intro: 'This section provides further information about the core hiding feature, so that you understand its legal nature and the boundaries of responsibility.',
        items: [
          'All processing the product performs on spoiler elements of supported sites is carried out locally within your browser.',
          'The product does not copy, transmit, or redistribute any content from third-party sites.',
          'The product does not bypass paywalls, obtain paid content, or evade subscription verification.',
          'The product does not modify server-side data of any third-party site.',
          'The product has no official partnership with the third-party sites referenced in section 3 of these Terms.',
          'Because third-party sites continually evolve their page structures, hiding rules may partially fail after site redesigns. The operator will adapt as quickly as possible, but does not guarantee real-time effectiveness at any given moment.',
          'Before using the hiding feature, please confirm that the laws of your jurisdiction and the target site\'s terms permit this kind of local processing. If you have any doubt, please stop using the relevant feature.',
          'The operator bears no responsibility for any third-party dispute, account suspension, or compliance issue arising from your use of the product\'s hiding feature. The associated compliance risk is borne by you personally.'
        ]
      },
      disclaimer: {
        title: '7. Disclaimers',
        items: [
          'The product is provided "AS IS" without any express or implied warranty of fitness for a particular purpose, stability, or uninterrupted operation',
          'The operator is not liable for hiding-rule failures caused by third-party site redesigns',
          'The operator is not liable for functional anomalies caused by differences in your device environment, network conditions, or browser version',
          'The operator is not liable for any indirect, incidental, special, or consequential damages arising from your use of the product',
          'In any event, the operator\'s aggregate liability under these Terms is capped at the amount you have paid for the product, if any; since the current phase involves paid subscription services, that amount is the fees you actually paid'
        ]
      },
      updates: {
        title: '8. Changes to these Terms',
        content: 'The operator may modify these Terms to reflect product evolution or changes in law. Modified Terms will be communicated through the website announcement and the in-extension "version updates" page. Continued use of the product constitutes acceptance of the modified Terms.'
      },
      dispute: {
        title: '9. Dispute resolution',
        content: 'Any dispute arising from or in connection with these Terms shall first be resolved through friendly negotiation. If negotiation fails, either party may submit the dispute to the competent people\'s court at the operator\'s location.'
      },
      law: {
        title: '10. Governing law',
        content: 'The interpretation, validity, and dispute resolution of these Terms are governed by the laws of the People\'s Republic of China (excluding Hong Kong SAR, Macau SAR, and Taiwan region for the purposes of these Terms).'
      }
    }
  },


refund: {
    title: 'Refund Policy',
    subtitle: 'Subscription refund rules and notes',
    meta: 'Effective · 2026-09-06',
    intro: 'This policy sets out the refund and compensation rules for the Replay Shield Pro subscription. Replay Shield is a virtual digital product: subscribing consumes service resources immediately, so "no-reason full refunds" are not offered. If you truly need a refund (and the operator has verified and enabled refund permission for your account), it is settled pro-rata by used days: refund = amount paid × days left ÷ plan days (rounded down). Used days include the activation day — there is no 100% refund. Please read this policy before subscribing; by paying, you acknowledge that you have read and agree to it.',
    sections: {
      scope: {
        title: '1. Scope',
        content: 'This policy applies to refunds and compensation for the Replay Shield Pro subscription. Trial, redeem-code, invite-reward, and operator-gifted or adjusted access involve no payment and are not refundable.'
      },
      window: {
        title: '2. Refund permission and entry',
        items: [
          'No active refund entry is offered on the user side by default. If you need a refund for special reasons (product issues, system errors, accidental purchase, etc.), describe the situation in Feedback in the browser extension.',
          'After verifying the situation, the operator may enable refund permission for your account in the admin console. Once enabled, any "paid" subscription order of yours with days remaining can be refunded self-service.',
          'Whether and when refund permission is granted is decided by the operator case by case and does not imply a refund commitment for any order.'
        ]
      },
      calc: {
        title: '3. Refund amount calculation',
        intro: 'Refunds are settled pro-rata by calendar days:',
        items: [
          'Used days = request date − activation date + 1 (includes the activation day; requesting on the activation day still counts as 1 used day — no 100% refund).',
          'Days left = plan days − used days. Orders with 0 or fewer days left are not refundable.',
          'Refund amount = amount paid × days left ÷ plan days (rounded down to cents). Example: requesting on day 3 of a 30-day monthly plan refunds 28/30 of the amount paid.',
          'The refund is based on the amount actually paid for this order (after any discount), not the plan list price; campaign/discounted purchases are refunded on the paid amount.',
          'If a product issue, system error, or bug affects your experience, submit a description through Feedback in the browser extension; after verification, the operator may grant Pro membership days as compensation (membership time, not cash) or enable refund permission under the rules above.'
        ]
      },
      process: {
        title: '4. Request and processing flow',
        items: [
          'Once refund permission is enabled, open "Orders" in My Account and click "Request refund" on a paid order in the order details.',
          'The dialog shows the full breakdown: activation date, plan days, used days (incl. today), days left, amount paid, and the refund for this request. Review it, tick the Refund Policy, and confirm.',
          'The refund is executed automatically to the original payment method after confirmation — no manual review. Please double-check before submitting; it cannot be undone.',
          'If the refund initiation fails (e.g. channel error), the order stays in "Refund in progress" and is handled manually by the operator in the retry queue; you may check the order status later.'
        ]
      },
      notApplicable: {
        title: '5. Not covered',
        items: [
          'Accounts without refund permission enabled by the operator.',
          'Orders whose paid service segment has been fully consumed (0 or fewer days left).',
          'Trial, redeem-code, invite-reward, and operator-gifted access (not paid).',
          'An order already fully refunded (terminal state; cannot be refunded again).'
        ]
      },
      coverage: {
        title: '6. Service scope and limitations',
        intro: 'Refunds involve access proration, so please also understand the coverage boundary of the service:',
        items: [
          'The product only provides spoiler hiding on configured video pages of supported sites; site homepages, listing pages, and sites outside the supported list are out of scope.',
          'Directly opened supported video pages can be hidden; pages opened consecutively from them (deeper pages, pages produced by in-site navigation) are not guaranteed.',
          'Supported third-party sites may redesign at any time (page structure, routing, player changes), which may cause hiding to fail, misalign, or partially break. The operator keeps adapting but does not guarantee complete coverage of any page at any moment.',
          'Network failures, login requirements, and regional restrictions may make the protection unavailable. When rules are missing or anomalous, the product shows a "protection temporarily unavailable" notice and still allows viewing; the operator is not liable for spoiler information being seen.'
        ]
      },
      compensation: {
        title: '7. Product-issue compensation',
        content: 'To protect Pro membership: if a product issue, system error, or bug affects your experience, submit a description through Feedback in the browser extension (including the issue type and description). Once we verify it as a real issue, Pro membership days are granted in real time (compensation is membership time, not a cash refund). The result and official reply can be viewed in "My Feedback" in the extension.'
      },
      rewards: {
        title: '8. Invite rewards and refunds',
        content: 'Invite rewards are granted based on a friend\'s actual subscription. If a friend\'s subscription order is refunded, the corresponding invite reward already granted is fully recovered (that record is marked refunded) and the inviter\'s access is recalculated from the remaining active records. Registration-level rewards (+5 days) are not recovered on a friend\'s refund.'
      },
      termination: {
        title: '9. Service termination',
        content: 'If the operator discontinues part or all of the subscription service, the operator will refund the user\'s unconsumed paid time in proportion under this policy.'
      },
      invoice: {
        title: '10. Invoices',
        content: 'The operator does not currently provide invoices. If you require an invoice, we suggest not subscribing for now.'
      },
      autoRenew: {
        title: '11. Auto-renewal',
        content: 'Auto-renewal is not currently offered. If auto-renewal is introduced later, we will provide prominent notice and confirmation and an easy way to cancel, in line with applicable law, and update this policy accordingly.'
      },
      dispute: {
        title: '12. Questions and contact',
        content: "If you disagree with a refund outcome, amount calculation, or order handling, contact us at woofootball@aliyun.com and we will re-check and respond within a reasonable time."
      }
    }
  },

  inviteRules: {
    title: 'Invite Rules',
    subtitle: 'How inviting works for Replay Shield Pro',
    meta: 'Updated · 2026-09-06',
    intro: 'Invite a friend to Replay Shield Pro and both of you earn rewards. This page explains how invite relationships are bound and how rewards are granted. Registering through an invite link means you have read and understood these rules.',
    sections: {
      bind: {
        title: '1. Binding rules',
        items: [
          'When a new account registers through an invite link (or invite code), the invite relationship with the inviter is established. It lasts for life and cannot be changed or removed.',
          'Attribution is valid for 24 hours only: registering within 24 hours of clicking the invite link counts toward the invite. After 24 hours the link expires and registration is bound to no inviter.',
          'Inviting yourself is invalid. Existing accounts placing orders through a link are not silently bound — if an unbound account checks out with an invite code, your confirmation is requested first.',
          'Each user has one invite code, viewable and copyable under "My Invites" in your account.'
        ]
      },
      discount: {
        title: '2. Invited discount',
        items: [
          'A friend bound to your invite gets the invited first-order discount on their first subscription. The page shows the current rate.',
          'The invited discount is mutually exclusive with limited-time campaigns and the first-order discount — no stacking. The most favorable option is applied automatically; when several candidates exist, you can choose one in the checkout (including no discount).',
          'Any first paid order — including at a campaign price — consumes first-order eligibility. It is not kept or deferred, and a refund does not restore it.'
        ]
      },
      reward: {
        title: '3. Invite rewards',
        items: [
          'Registration reward: when a friend registers through your invite, you immediately earn 5 membership days (capped at 365 days per calendar year from registration rewards).',
          'Subscription reward: when a friend\'s first subscription succeeds (any plan), you earn half that plan\'s duration, rounded up (15 days for 30-day monthly, 45 days for 90-day quarterly, 183 days for 365-day yearly).',
          'The subscription reward is granted once per friend (first subscription only); renewals do not trigger it.'
        ]
      },
      refund: {
        title: '4. Refunds and recovery',
        items: [
          'If a friend\'s first subscription order is refunded, the subscription reward you earned from that order is fully retracted. The access history is marked "Retracted" and your membership expiry is recalculated.',
          'Registration rewards (+5 days) are not retracted on a friend\'s refund.',
          'A friend\'s refund is settled pro-rata by days; it does not change how your reward is recovered — no portion of the reward is kept for the time the friend actually used.'
        ]
      },
      display: {
        title: '5. Checking your rewards',
        content: 'Under "My Invites" in your account you can view your invite code, invite link, and the full invite reward history. In the access history, each invite reward shows its source right on the record (node · masked friend account · plan), and retracted rewards are marked "Retracted".'
      }
    }
  },

  notFound: {
    title: 'Page not found',
    desc: 'The page you are looking for does not exist or has been removed.',
    backHome: 'Back to home'
  }
} as const
